<template>
    <div class="container-fluid px-5 mt-4">

        <!-- Nadpis stránky + tlačítka -->
        <div class="d-flex align-items-center justify-content-between mb-4">
            <h1 class="credits-page__title">{{ $t('file.files') }}</h1>
            <div class="d-flex gap-2">
                <button class="btn certs-add-folder-btn" @click="showAddFolderModal = true">
                    {{ $t('cert.addFolder') }}
                    <img :src="iconFolder" alt="" class="certs-add-folder-btn__icon">
                </button>
                <button class="btn btn-primary" style="height:40px;padding:0 16px;font-size:14px;font-weight:500;" @click="showUploadModal = true">
                    {{ $t('file.uploadNewFile') }}
                </button>
            </div>
        </div>

        <!-- Sekce Moje složky -->
        <div class="dashboard-section-header mb-3">
            <div class="d-flex align-items-center gap-1 flex-wrap folder-breadcrumb">
                <img :src="iconFolder" alt="" class="dashboard-section-header__icon me-1" style="width:14px;height:14px;filter:brightness(0) invert(88%) sepia(6%) saturate(362%) hue-rotate(202deg) brightness(93%) contrast(87%)">
                <span v-if="!folderPath.length" class="dashboard-section-header__title">{{ $t('cert.myFolders') }}</span>
                <template v-else>
                    <button class="folder-breadcrumb__link" @click="navigateTo(-1)">{{ $t('cert.myFolders') }}</button>
                    <template v-for="(f, i) in folderPath" :key="f.uuid">
                        <i class="bi bi-chevron-right folder-breadcrumb__sep"></i>
                        <button v-if="i < folderPath.length - 1" class="folder-breadcrumb__link" @click="navigateTo(i)">{{ f.name }}</button>
                        <span v-else class="dashboard-section-header__title">{{ f.name }}</span>
                    </template>
                </template>
                <span class="count-badge ms-1">{{ folders.length }}</span>
            </div>
            <button v-if="folders.length > 3 && !showAllFolders" type="button" class="dashboard-show-all" @click="showAllFolders = true">
                {{ $t('cert.viewAll') }}
                <i class="bi bi-chevron-right dashboard-show-all__chevron"></i>
            </button>
        </div>

        <!-- Složky jako karty -->
        <div v-if="!foldersLoading && folders.length > 0" class="folder-cards-row mb-5">
            <div
                v-for="folder in visibleFolders"
                :key="folder.uuid"
                class="folder-card dashboard-action-box--clickable"
                :class="{ 'folder-card--active': selectedFolder && selectedFolder.uuid === folder.uuid }"
                @click="selectFolder(folder)"
            >
                <div class="folder-card__icon">
                    <img :src="iconFolder" alt="" style="filter:brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)">
                </div>
                <p class="folder-card__name">{{ folder.name }}</p>
                <p class="folder-card__count">&nbsp;</p>
            </div>
        </div>

        <!-- Empty state složky -->
        <div v-if="!foldersLoading && folders.length === 0" class="d-flex justify-content-center mt-4 mb-5">
            <EmptyStateComponent
                :icon="iconFolder"
                :icon-style="{ width: '17px', height: '17px' }"
                :title="$t('cert.emptyFoldersTitle')"
                :text="$t('cert.emptyFoldersText')"
            >
                <button class="btn certs-add-folder-btn" @click="showAddFolderModal = true">
                    {{ $t('cert.addFolder') }}
                    <img :src="iconFolder" alt="" class="certs-add-folder-btn__icon">
                </button>
            </EmptyStateComponent>
        </div>

        <!-- Modal Přidat složku -->
        <div v-if="showAddFolderModal" class="create-cert-overlay" @click.self="showAddFolderModal = false">
            <div class="create-cert-modal" style="max-width:400px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('cert.addFolder') }}</span>
                    <button class="create-cert-modal__close" @click="showAddFolderModal = false">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <label class="create-cert-label mb-1">{{ $t('file.name') }}</label>
                    <input
                        type="text"
                        class="form-control mb-4"
                        v-model="newFolderName"
                        :placeholder="$t('file.newFolder')"
                    >
                    <div class="d-flex justify-content-end">
                        <button class="btn btn-primary" @click="createFolder" :disabled="!newFolderName">
                            {{ $t('file.create') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal Nahrát soubor -->
        <div v-if="showUploadModal" class="create-cert-overlay" @click.self="closeUploadModal">
            <div class="create-cert-modal" style="max-width:480px;">
                <div class="create-cert-modal__header">
                    <span class="create-cert-modal__title">{{ $t('file.uploadNewFile') }}</span>
                    <button class="create-cert-modal__close" @click="closeUploadModal">×</button>
                </div>
                <div class="create-cert-modal__body">
                    <div
                        class="create-cert-dropzone"
                        :class="{ 'create-cert-dropzone--active': isDragging, 'create-cert-dropzone--filled': uploadFile }"
                        @dragover.prevent="isDragging = true"
                        @dragleave.prevent="isDragging = false"
                        @drop.prevent="onDrop"
                        @click="$refs.fileInput.click()"
                    >
                        <img :src="iconFile" alt="" class="create-cert-dropzone__icon">
                        <span v-if="!uploadFile" class="create-cert-dropzone__text">{{ $t('cert.uploadFile') }}</span>
                        <span v-else class="create-cert-dropzone__filename">{{ uploadFile.name }}</span>
                        <input type="file" ref="fileInput" style="display:none" @change="onFileChanged">
                    </div>
                    <p class="create-cert-hint mt-2">{{ $t('cert.maxFileSizeText') }}</p>

                    <div v-if="selectedFolder" class="mt-3">
                        <span class="create-cert-label">{{ $t('file.folder') }}: </span>
                        <span class="fw-semibold">{{ selectedFolder.name }}</span>
                    </div>

                    <div class="d-flex justify-content-end gap-3 mt-4">
                        <button class="btn create-cert-btn-cancel" type="button" @click="closeUploadModal">
                            {{ $t('general.cancel') }}
                        </button>
                        <button class="btn btn-primary create-cert-btn-submit" type="button" @click="uploadFileStandalone" :disabled="uploading || !uploadFile">
                            <span v-if="uploading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            {{ uploading ? $t('cert.processing') : $t('file.uploadNewFile') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Sekce Nahrané soubory / obsah složky -->
        <div class="dashboard-section-header mb-3 mt-2">
            <div class="d-flex align-items-center gap-2">
                <img :src="iconFile" alt="" class="dashboard-section-header__icon" style="width:14px;height:14px;filter:brightness(0) invert(88%) sepia(6%) saturate(362%) hue-rotate(202deg) brightness(93%) contrast(87%)">
                <span class="dashboard-section-header__title">{{ filesSectionTitle }}</span>
                <span class="count-badge ms-2">{{ totalCount }}</span>
            </div>
            <button v-if="selectedFolder" class="btn cert-list__view-btn" @click="navigateTo(-1)">
                <i class="bi bi-chevron-left me-1"></i>{{ $t('file.allFiles') }}
            </button>
        </div>

        <Skeletor v-if="loading" />

        <!-- Empty state -->
        <div v-if="!loading && !filteredFiles.length" class="d-flex justify-content-center mt-5 pt-4">
            <EmptyStateComponent
                :icon="iconFile"
                :icon-style="{ width: '17px', height: '17px', filter: 'brightness(0) invert(24%) sepia(87%) saturate(5481%) hue-rotate(213deg) brightness(101%) contrast(106%)' }"
                :title="$t('file.emptyFilesTitle')"
                :text="$t('file.emptyFilesText')"
            >
                <button class="btn btn-primary app-nav__create-btn" @click="showUploadModal = true">
                    {{ $t('file.uploadNewFile') }} <img :src="iconPlus" alt="">
                </button>
            </EmptyStateComponent>
        </div>

        <div v-if="!loading && filteredFiles.length" class="cert-list cert-list--certs-mobile">
            <div
                class="cert-list__row"
                v-for="file in filteredFiles"
                :key="file.uuid"
            >
                <div class="cert-list__icon-wrap">
                    <img :src="iconFile" alt="" class="cert-list__file-icon">
                </div>

                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('file.fileName') }}</span>
                    <span class="cert-list__value">{{ file.name }}</span>
                </div>

                <div class="cert-list__col cert-list__col--date">
                    <span class="cert-list__label">{{ $t('file.fileCreatedDate') }}</span>
                    <span class="cert-list__value">{{ formatDateShort(file.created_at) }}, {{ formatTime(file.created_at) }}</span>
                </div>

                <div class="cert-list__col">
                    <span class="cert-list__label">{{ $t('file.folder') }}</span>
                    <span class="cert-list__value">{{ file.folder_name ?? '—' }}</span>
                </div>

                <div class="cert-list__action">
                    <router-link
                        class="btn cert-list__view-btn"
                        :to="{ name: 'FileDetail', params: { uuid: file.uuid } }"
                    >
                        {{ $t('file.viewFile') }}
                    </router-link>

                    <button
                        v-if="!file.cert_id"
                        class="btn cert-list__view-btn"
                        @click="generateCertFromFile(file)"
                        :disabled="generatingCertForFile === file.uuid"
                    >
                        <span v-if="generatingCertForFile === file.uuid" class="spinner-border spinner-border-sm me-1" role="status"></span>
                        {{ $t('cert.certCreate') }}
                    </button>

                    <router-link
                        v-if="file.cert_id && file.cert"
                        class="btn cert-list__view-btn"
                        :to="{ name: 'CertDetail', params: { uuid: file.cert.uuid } }"
                    >
                        {{ $t('cert.viewCertificate') }}
                    </router-link>
                </div>
            </div>
        </div>

        <!-- Načíst více -->
        <div v-if="!loading && loadMoreAvailable" class="d-flex justify-content-center mt-4 mb-5">
            <button class="btn cert-list__view-btn" @click="loadMore">
                {{ $t('file.loadMore') }}
            </button>
        </div>

    </div>
</template>

<script>
import http from '@/http';
import { loadingState } from '@/loading.js';
import iconFile from '/public/img/new-design/icons/file.svg';
import iconFolderFill from '/public/img/new-design/icons/folder-fill.svg';
import iconFolderBlue from '/public/img/new-design/icons/folder-fill-blue.svg';
import iconMagnifying from '/public/img/new-design/icons/magnifying.svg';
import iconPlus from '/public/img/new-design/icons/plus.svg';
import iconFolder from '/public/img/new-design/icons/folder.svg';

export default {
    mounted() {
        this.fetchItems();
        this.emitter.on('fileUploaded', (raw) => {
            const file = typeof raw === 'string' ? JSON.parse(raw) : raw;
            if (file && file.uuid) {
                this.filesList.unshift(file);
                this.totalCount++;
            }
        });
    },

    data() {
        return {
            folders: [],
            folderPath: [],
            filesList: [],
            loading: true,
            foldersLoading: true,
            selectedFolder: null,
            showAddFolderModal: false,
            showAllFolders: false,
            showUploadModal: false,
            newFolderName: '',
            uploadFile: null,
            isDragging: false,
            uploading: false,
            searchQuery: '',
            page: 1,
            perPage: 20,
            totalCount: 0,
            loadMoreAvailable: false,
            generatingCertForFile: null,
            iconFile,
            iconFolderFill,
            iconFolderBlue,
            iconMagnifying,
            iconPlus,
            iconFolder,
        };
    },

    computed: {
        visibleFolders() {
            return this.showAllFolders ? this.folders : this.folders.slice(0, 4);
        },

        filesSectionTitle() {
            return this.selectedFolder
                ? this.$t('file.uploadedFilesIn') + ' ' + this.selectedFolder.name
                : this.$t('file.uploadedFiles');
        },

        filteredFiles() {
            const q = this.searchQuery.trim().toLowerCase();
            if (!q) return this.filesList;
            return this.filesList.filter(f => f.name?.toLowerCase().includes(q));
        },
    },

    methods: {
        fetchItems(append = false) {
            if (!append) {
                this.loading = true;
                this.foldersLoading = true;
            }

            const uuid = this.selectedFolder?.uuid;
            const path = uuid ? `/files/folders/${uuid}` : '/files/folders';

            http.get(path, { params: { perPage: this.perPage, page: this.page } }).then((response) => {
                const items = response.data.files || [];
                const folders = items.filter(f => f.type === 'FOLDER');
                const files = items.filter(f => f.type !== 'FOLDER');

                this.folders = folders;

                if (append) {
                    this.filesList = this.filesList.concat(files);
                } else {
                    this.filesList = files;
                }

                this.loadMoreAvailable = files.length === this.perPage;
                this.totalCount = this.filesList.length;
                this.loading = false;
                this.foldersLoading = false;
                loadingState.active = false;
            });
        },

        loadMore() {
            this.page++;
            this.fetchItems(true);
        },

        selectFolder(folder) {
            this.folderPath.push(folder);
            this.selectedFolder = folder;
            this.page = 1;
            this.filesList = [];
            this.showAllFolders = false;
            this.fetchItems();
        },

        navigateTo(index) {
            if (index === -1) {
                this.selectedFolder = null;
                this.folderPath = [];
            } else {
                this.selectedFolder = this.folderPath[index];
                this.folderPath = this.folderPath.slice(0, index + 1);
            }
            this.page = 1;
            this.filesList = [];
            this.showAllFolders = false;
            this.fetchItems();
        },

        createFolder() {
            if (!this.newFolderName) return;
            http.post('/files/folders/create', { name: this.newFolderName }).then((response) => {
                if (response.data.file) {
                    this.folders.unshift(response.data.file);
                }
                this.showAddFolderModal = false;
                this.newFolderName = '';
            });
        },

        onDrop(event) {
            this.isDragging = false;
            const file = event.dataTransfer.files[0];
            if (file) this.setUploadFile(file);
        },

        onFileChanged(event) {
            const file = event.target.files[0];
            if (file) this.setUploadFile(file);
        },

        setUploadFile(file) {
            if (file.size > 31.2 * 1024 * 1024) {
                alert(this.$t('file.fileTooLarge'));
                return;
            }
            this.uploadFile = file;
        },

        closeUploadModal() {
            this.showUploadModal = false;
            this.uploadFile = null;
            this.isDragging = false;
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },

        uploadFileStandalone() {
            if (!this.uploadFile) return;
            this.uploading = true;

            const body = new FormData();
            body.append('file', this.uploadFile);
            if (this.selectedFolder) {
                body.append('folderUuid', this.selectedFolder.uuid);
            }

            http.post('/files/upload', body, { headers: { 'Content-Type': 'multipart/form-data' } })
                .then((response) => {
                    const file = response.data?.file || response.data;
                    if (file && file.uuid) {
                        this.filesList.unshift(file);
                        this.totalCount++;
                    }
                    this.uploading = false;
                    this.closeUploadModal();
                })
                .catch(() => {
                    this.uploading = false;
                });
        },

        generateCertFromFile(file) {
            this.generatingCertForFile = file.uuid;
            http.post('/certs/generate', { file_model: file })
                .then((response) => {
                    const updatedFile = response.data.file;
                    if (updatedFile) {
                        const idx = this.filesList.findIndex(f => f.uuid === file.uuid);
                        if (idx !== -1) this.filesList[idx] = updatedFile;
                    }
                    this.generatingCertForFile = null;
                })
                .catch(() => {
                    this.generatingCertForFile = null;
                });
        },

        formatDateShort(dateStr) {
            const d = new Date(dateStr);
            const days = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'];
            return `${days[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
        },

        formatTime(dateStr) {
            const d = new Date(dateStr);
            const pad = n => String(n).padStart(2, '0');
            return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },
    },
};
</script>

<style scoped>
.files-upload-btn {
    display: flex;
    align-items: center;
    gap: 8px;
}

.folder-card--active {
    border-color: #175cff !important;
    background: #f0f5ff;
}
</style>